// WAI-ARIA roving-tabindex hook for `role="toolbar"` / `radiogroup` /
// `tablist`. Hoisted at the 2nd consumer (PreviewToolbar +
// OutputToolbar) below the standard 3-use threshold because the
// keyboard logic is non-trivial and lockstep correctness matters for
// a11y. Selector is intentionally narrower than the WAI-ARIA tabbable
// set — toolbars cycle interactive controls only, not anchors/inputs.

import { useCallback, useEffect, useRef, useState } from 'preact/hooks'

const FOCUSABLE_ITEM_SELECTOR =
  'button:not([disabled]):not([aria-disabled="true"]), ' +
  '[role="radio"]:not([aria-disabled="true"]), ' +
  '[role="tab"]:not([aria-disabled="true"])'

export interface UseRovingTabIndexOptions {
  readonly defaultIndex?: number
}

export interface UseRovingTabIndex<E extends HTMLElement> {
  readonly rootRef: { current: E | null }
  readonly activeIndex: number
  readonly setActiveIndex: (i: number) => void
}

export function useRovingTabIndex<E extends HTMLElement = HTMLElement>(
  options: UseRovingTabIndexOptions = {},
): UseRovingTabIndex<E> {
  const { defaultIndex = 0 } = options
  const rootRef = useRef<E | null>(null)
  const [activeIndex, setActiveIndex] = useState(defaultIndex)

  const moveTo = useCallback((nextIndex: number) => {
    const root = rootRef.current
    /* v8 ignore next -- moveTo is only called from the keydown handler, which already guards on root before installing; defensive */
    if (!root) return
    const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_ITEM_SELECTOR))
    /* v8 ignore next -- moveTo entry follows a keydown handler that already verified items.length > 0 to compute currentIndex; defensive */
    if (items.length === 0) return
    const safe = ((nextIndex % items.length) + items.length) % items.length
    setActiveIndex(safe)
    items[safe]?.focus()
  }, [])

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const onKeyDown = (e: KeyboardEvent) => {
      const items = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_ITEM_SELECTOR))
      /* v8 ignore next -- listener is only installed when root is mounted with children; an empty toolbar never receives a keydown because nothing is focusable to dispatch from */
      if (items.length === 0) return
      const currentIndex = items.indexOf(document.activeElement as HTMLElement)
      if (currentIndex < 0) return
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          moveTo(currentIndex + 1)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          moveTo(currentIndex - 1)
          break
        case 'Home':
          e.preventDefault()
          moveTo(0)
          break
        case 'End':
          e.preventDefault()
          moveTo(items.length - 1)
          break
      }
    }
    root.addEventListener('keydown', onKeyDown)
    return () => {
      root.removeEventListener('keydown', onKeyDown)
    }
  }, [moveTo])

  return { rootRef, activeIndex, setActiveIndex }
}
